import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../react-query.config';
import { useUser } from '@/contexts/UserContext';

/** Keep play/detail status fresh in mobile webviews (no manual refresh). */
const PICKEM_STATUS_STALE_TIME_MS = 30_000;
const PICKEM_STATUS_REFETCH_INTERVAL_MS = 30_000;

const pickemStatusQueryOptions = {
  staleTime: PICKEM_STATUS_STALE_TIME_MS,
  refetchInterval: PICKEM_STATUS_REFETCH_INTERVAL_MS,
  refetchIntervalInBackground: false,
} as const;

export const usePlayPickems = () => {
  const { user, isLoading } = useUser();
  const location = user?.location;

  return useQuery({
    queryKey: queryKeys.pickem.play(location),
    queryFn: async () => {
      const params = {
        selections: 'true',
        ...(location && { location }),
      };
      return apiClient.get('/api/pickems/play', { params });
    },
    enabled: !isLoading, // Wait for user context to be ready
    ...pickemStatusQueryOptions,
  });
};

export const usePickemById = (pickemId: string) => {
  const { user, isLoading } = useUser();
  const location = user?.location;

  return useQuery({
    queryKey: queryKeys.pickem.detail(pickemId, location),
    queryFn: async () => {
      return apiClient.get('/api/pickems/{id}', {
        pathParams: { id: pickemId },
        params: location ? { location } : undefined,
      });
    },
    enabled: !!pickemId && !isLoading, // Wait for user context and ensure pickemId exists
    ...pickemStatusQueryOptions,
  });
};

export const useTodayPickems = () => {
  const { user, isLoading } = useUser();
  const location = user?.location;

  return useQuery({
    queryKey: queryKeys.pickem.today(location),
    queryFn: async () => {
      return apiClient.get('/api/pickems/today', {
        params: {
          selections: 'true',
          ...(location && { location }),
        },
      });
    },
    enabled: !isLoading, // Wait for user context to be ready
    ...pickemStatusQueryOptions,
  });
};

export const usePickemMarketingConsent = (pickemId: string, enabled = true) => {
  return useQuery({
    queryKey: [...queryKeys.pickem.all, 'marketing-consent', pickemId] as const,
    queryFn: async () => {
      return (apiClient.get as (path: string) => Promise<PickemMarketingConsentResponse>)(
        `/api/pickems/${pickemId}/marketing-consent`
      );
    },
    enabled: !!pickemId && enabled,
    staleTime: 1000 * 60 * 15,
  });
};

export interface MarketingConsentResponse {
  id: string;
  title: string;
  version?: string | null;
  consent_text: string;
  header_image_url?: string | null;
  assets?: { url: string; filename?: string; label?: string }[];
}

export interface PickemMarketingConsentResponse {
  consent: MarketingConsentResponse | null;
  marketing_consent_given: boolean | null;
}

export const useRecordMarketingConsent = (pickemId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (marketingConsentGiven: boolean) => {
      return (
        apiClient.post as (
          path: string,
          body: { marketing_consent_given: boolean }
        ) => Promise<{ success: boolean; marketing_consent_given: boolean | null }>
      )(`/api/pickems/${pickemId}/marketing-consent`, {
        marketing_consent_given: marketingConsentGiven,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.pickem.all, 'marketing-consent', pickemId],
      });
    },
  });
};

export const usePickemRules = (pickemId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.pickem.rules(pickemId),
    queryFn: async () => {
      return apiClient.get('/api/pickems/{id}/rules', {
        pathParams: { id: pickemId },
      });
    },
    enabled: !!pickemId && enabled,
    staleTime: 1000 * 60 * 15,
  });
};

export const useRecentPickems = () => {
  const { user, isLoading } = useUser();
  const location = user?.location;

  return useQuery({
    queryKey: queryKeys.pickem.recent(location),
    queryFn: async () => {
      // 25 is plenty for the "completed games" list (display-only, sorted by
      // date). 100 shipped a much larger payload + larger server-side cache blob
      // for no UX benefit.
      const params = location ? { location, limit: 25 } : { limit: 25 };

      return apiClient.get('/api/pickems/recent', {
        params,
      });
    },
    enabled: !isLoading, // Wait for user context to be ready
    // Poll less aggressively: recent is mostly completed, but picks up
    // newly finished contests without requiring a webview remount.
    staleTime: PICKEM_STATUS_STALE_TIME_MS,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
};
